import React, { useState, useEffect } from "react";
import { Modal } from "../Common/Modal";
import { HiShare, HiDownload } from "react-icons/hi";
import api from "../../services/api";

interface TranscriptMessage {
  role: string;
  content: string;
}

interface TranscriptModalProps {
  callLog: {
    id: number;
    contact_name: string;
    duration: number;
    call_transcript_url: string | null;
  };
  onClose: () => void;
}

export const TranscriptModal: React.FC<TranscriptModalProps> = ({ callLog, onClose }) => {
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareMessage, setShareMessage] = useState<string>("");

  useEffect(() => {
    fetchTranscript();
  }, [callLog.id]);

  const fetchTranscript = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/campaigns/call-logs/${callLog.id}/transcript`);
      setTranscript(response.data.messages);
    } catch (error) {
      console.error("Error fetching transcript:", error);
      setError("Failed to load transcript");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      // Generate universal URL (remove admin prefix for cross-user compatibility)
      let shareUrl = window.location.href;
      if (shareUrl.includes('/admin/campaigns/')) {
        shareUrl = shareUrl.replace('/admin/campaigns/', '/campaigns/');
      }
      
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage("Link copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      setShareMessage("Failed to copy link");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  return (
    <Modal 
      isOpen={true} 
      title={`Call Transcript - ${callLog.contact_name}`} 
      onClose={onClose}
    >
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">Loading transcript...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">{error}</div>
        ) : (
          <div className="space-y-4">

            {/* Transcript messages */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {transcript.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === "assistant" ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.role === "assistant"
                        ? "bg-[var(--muted)] text-[var(--foreground)]"
                        : "bg-primary text-primary-foreground"
                    }`}
                  >
                    <div className="text-xs font-medium mb-1 opacity-75">
                      {message.role === "assistant" ? "Agent" : "Customer"}
                    </div>
                    <div className="text-sm">{message.content}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="mt-4 pt-4 border-t border-[var(--border)]">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 text-[var(--foreground)] hover:text-primary border border-[var(--border)] rounded-lg hover:bg-[var(--muted)] transition-colors"
                  title="Copy link"
                >
                  <HiShare className="h-4 w-4" />
                  Copy Link
                </button>
                
                <button
                  onClick={() => {
                    const text = transcript
                      .map(m => `${m.role === "assistant" ? "Agent" : "Customer"}: ${m.content}`)
                      .join("\n\n");
                    
                    const blob = new Blob([text], { type: "text/plain" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `transcript-${callLog.contact_name.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  <HiDownload className="h-4 w-4" />
                  Export Transcript
                </button>
              </div>
              
              {/* Share message */}
              {shareMessage && (
                <div className="mt-2 text-center text-sm text-green-600">
                  {shareMessage}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}; 